import { CalcDecimal, decimalString } from "./decimal.js";

export const DM2016_FOOTNOTE_RESOLVER_VERSION = "0.1.0";

const SERVICE_CODE = /^Q(?:a\.0|[a-e](?:I|II|III|IV))\.\d{2}(?:\.\d{2})?$/;
const DECIMAL = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
const PLANNING_CODES = /^Qa\.0\.0[1-7]$/;

export type Dm2016PlanningValueBasis =
  | "territorial_gdp"
  | "existing_and_design_volumes"
  | "farm_gross_saleable_production";

export interface Dm2016FootnoteServiceInput {
  readonly serviceCode: string;
  readonly valueV?: string;
}

export interface Dm2016FootnoteContext {
  readonly procurementModel?: "non_integrated" | "integrated";
  readonly tenderBaseProject?: boolean;
  readonly publicWorksConcession?: boolean;
  readonly planning?: {
    readonly level: "general" | "executive";
    readonly valueBasis: Dm2016PlanningValueBasis;
  };
  readonly variationAbsoluteIncrease?: string;
  readonly variationAbsoluteDecrease?: string;
  readonly newlyDesignedWorksGrossValue?: string;
  readonly collaudoInCorsoDOpera?: boolean;
  readonly energyDiagnosisDocumentationAvailable?: boolean;
}

export interface Dm2016ResolvedFootnoteService {
  readonly serviceCode: string;
  readonly valueV: string;
  readonly multiplier: "1" | "1.2";
  readonly appliedFootnotes: readonly number[];
}

export interface Dm2016FootnoteResolution {
  readonly resolverVersion: typeof DM2016_FOOTNOTE_RESOLVER_VERSION;
  readonly services: readonly Dm2016ResolvedFootnoteService[];
}

export interface Dm2016FootnoteIssue {
  readonly code: string;
  readonly serviceCode: string;
  readonly footnote: number;
  readonly message: string;
}

export class Dm2016FootnoteInputError extends Error {
  readonly issues: readonly Dm2016FootnoteIssue[];

  constructor(issues: readonly Dm2016FootnoteIssue[]) {
    super("D.M. 2016 footnote conditions are incomplete or inconsistent");
    this.name = "Dm2016FootnoteInputError";
    this.issues = Object.freeze([...issues]);
  }
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

function decimal(value: string | undefined, label: string, allowZero = false): string {
  if (value === undefined || !DECIMAL.test(value)) {
    throw new Error(`${label} must be a plain decimal string`);
  }
  const parsed = new CalcDecimal(value);
  if (allowZero ? parsed.lt(0) : parsed.lte(0)) {
    throw new Error(`${label} must be ${allowZero ? "non-negative" : "positive"}`);
  }
  return decimalString(parsed);
}

function addIssue(
  issues: Dm2016FootnoteIssue[],
  serviceCode: string,
  footnote: number,
  code: string,
  message: string,
): void {
  issues.push({ code, serviceCode, footnote, message });
}

function resolvePlanning(
  context: Dm2016FootnoteContext,
  serviceCode: string,
  issues: Dm2016FootnoteIssue[],
): void {
  const planning = context.planning;
  if (planning === undefined) {
    addIssue(
      issues,
      serviceCode,
      2,
      "PLANNING_VALUE_BASIS_REQUIRED",
      "Indicare livello e base del valore V per la pianificazione.",
    );
    return;
  }
  const allowed = planning.level === "general"
    ? planning.valueBasis === "territorial_gdp"
    : planning.valueBasis === "existing_and_design_volumes"
      || planning.valueBasis === "farm_gross_saleable_production";
  if (!allowed) {
    addIssue(
      issues,
      serviceCode,
      2,
      "PLANNING_VALUE_BASIS_INCONSISTENT",
      "La base V non è coerente con il livello generale o esecutivo dichiarato.",
    );
  }
}

function requireCondition(
  condition: boolean,
  issues: Dm2016FootnoteIssue[],
  serviceCode: string,
  footnote: number,
  code: string,
  message: string,
): void {
  if (!condition) addIssue(issues, serviceCode, footnote, code, message);
}

export function resolveDm2016FootnoteEffects(input: {
  readonly services: readonly Dm2016FootnoteServiceInput[];
  readonly context: Dm2016FootnoteContext;
}): Dm2016FootnoteResolution {
  if (!Array.isArray(input.services) || input.services.length === 0) {
    throw new Dm2016FootnoteInputError([{
      code: "SERVICES_REQUIRED",
      serviceCode: "*",
      footnote: 0,
      message: "Indicare almeno una prestazione.",
    }]);
  }

  const issues: Dm2016FootnoteIssue[] = [];
  const codes = new Set<string>();
  input.services.forEach((service) => {
    if (!SERVICE_CODE.test(service.serviceCode)) {
      addIssue(issues, service.serviceCode, 0, "INVALID_SERVICE_CODE", "Codice prestazione non valido.");
    }
    if (codes.has(service.serviceCode)) {
      addIssue(issues, service.serviceCode, 0, "DUPLICATE_SERVICE", "Prestazione duplicata.");
    }
    codes.add(service.serviceCode);
  });

  const resolved = input.services.map((service): Dm2016ResolvedFootnoteService => {
    const { serviceCode } = service;
    const appliedFootnotes: number[] = [];
    let multiplier: "1" | "1.2" = "1";
    let valueV = "0";

    if (PLANNING_CODES.test(serviceCode)) {
      appliedFootnotes.push(2);
      resolvePlanning(input.context, serviceCode, issues);
    }

    if (serviceCode === "QbI.04") {
      appliedFootnotes.push(3);
      requireCondition(
        input.context.publicWorksConcession === true,
        issues,
        serviceCode,
        3,
        "PUBLIC_WORKS_CONCESSION_REQUIRED",
        "QbI.04 è applicabile soltanto in presenza di concessione di lavori pubblici.",
      );
    }
    if (serviceCode === "QbI.05") {
      appliedFootnotes.push(4);
      requireCondition(
        input.context.procurementModel === "integrated"
          || input.context.publicWorksConcession === true,
        issues,
        serviceCode,
        4,
        "INTEGRATED_OR_CONCESSION_REQUIRED",
        "QbI.05 richiede appalto integrato o concessione di lavori pubblici.",
      );
    }
    if (serviceCode === "QbI.14") {
      appliedFootnotes.push(6);
      requireCondition(
        input.context.tenderBaseProject === true
          || input.context.publicWorksConcession === true,
        issues,
        serviceCode,
        6,
        "TENDER_BASE_OR_CONCESSION_REQUIRED",
        "QbI.14 richiede progetto a base di gara o concessione di lavori pubblici.",
      );
    }
    if (serviceCode === "QbII.08") {
      appliedFootnotes.push(7);
      requireCondition(
        input.context.procurementModel === "non_integrated",
        issues,
        serviceCode,
        7,
        "NON_INTEGRATED_PROCUREMENT_REQUIRED",
        "QbII.08 non si applica nell’appalto integrato.",
      );
      requireCondition(
        input.context.tenderBaseProject === true,
        issues,
        serviceCode,
        7,
        "TENDER_BASE_PROJECT_REQUIRED",
        "QbII.08 richiede un progetto posto a base di gara.",
      );
    }

    try {
      if (serviceCode === "QcI.07") {
        appliedFootnotes.push(10);
        const increases = decimal(
          input.context.variationAbsoluteIncrease,
          "Variation absolute increase",
          true,
        );
        const decreases = decimal(
          input.context.variationAbsoluteDecrease,
          "Variation absolute decrease",
          true,
        );
        valueV = decimalString(new CalcDecimal(increases).plus(decreases));
        if (new CalcDecimal(valueV).lte(0)) throw new Error("Variation value must be positive");
        if (
          service.valueV !== undefined
          && decimal(service.valueV, `V for ${serviceCode}`) !== valueV
        ) {
          throw new Error("V for QcI.07 must equal the absolute increases plus decreases");
        }
      } else if (serviceCode === "QcI.08") {
        appliedFootnotes.push(11);
        requireCondition(
          codes.has("QcI.07"),
          issues,
          serviceCode,
          11,
          "QCI07_REQUIRED",
          "QcI.08 è aggiuntiva e richiede la presenza di QcI.07.",
        );
        valueV = decimal(
          input.context.newlyDesignedWorksGrossValue,
          "Newly designed works gross value",
        );
        if (
          service.valueV !== undefined
          && decimal(service.valueV, `V for ${serviceCode}`) !== valueV
        ) {
          throw new Error("V for QcI.08 must equal the gross value of newly designed works");
        }
      } else {
        valueV = decimal(service.valueV, `V for ${serviceCode}`);
      }
    } catch (error) {
      addIssue(
        issues,
        serviceCode,
        serviceCode === "QcI.07" ? 10 : serviceCode === "QcI.08" ? 11 : 0,
        "VALUE_INPUT_REQUIRED",
        error instanceof Error ? error.message : "Valore V non valido.",
      );
      valueV = "0";
    }

    if (serviceCode === "QdI.01") {
      appliedFootnotes.push(12);
      if (input.context.collaudoInCorsoDOpera === undefined) {
        addIssue(
          issues,
          serviceCode,
          12,
          "COLLAUDO_TIMING_REQUIRED",
          "Indicare se il collaudo avviene in corso d’opera.",
        );
      } else if (input.context.collaudoInCorsoDOpera) {
        multiplier = "1.2";
      }
    }

    if (serviceCode === "QdI.05") {
      appliedFootnotes.push(13);
      if (input.context.energyDiagnosisDocumentationAvailable === undefined) {
        addIssue(
          issues,
          serviceCode,
          13,
          "ENERGY_DIAGNOSIS_STATUS_REQUIRED",
          "Indicare se la diagnosi energetica è già disponibile.",
        );
      } else if (
        !input.context.energyDiagnosisDocumentationAvailable
        && !codes.has("QbII.22")
      ) {
        addIssue(
          issues,
          serviceCode,
          13,
          "QBII22_REQUIRED",
          "In assenza della diagnosi energetica occorre aggiungere QbII.22 con il proprio valore V esplicito.",
        );
      }
    }

    return {
      serviceCode,
      valueV,
      multiplier,
      appliedFootnotes: Object.freeze(appliedFootnotes),
    };
  });

  if (issues.length > 0) throw new Dm2016FootnoteInputError(issues);
  return freezeDeep({
    resolverVersion: DM2016_FOOTNOTE_RESOLVER_VERSION,
    services: resolved,
  });
}
